import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import Button from "@components/ui/buttons/Button";
import { useState, useRef } from "react";
import TextField from "@components/ui/textfields/TextField";
import { ContactInfos } from "types";
import { showNotification } from "context/notification.service";
import { usersApi } from "@services/api/users";

export const CreateContactModal = ({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (contact: ContactInfos) => void;
}) => {
  const [contact, setContact] = useState<ContactInfos>({
    first_name: "",
    last_name: "",
    prefix: "M.",
  });

  type UserSuggestion = { id: string; display_name: string; email: string; photo_url?: string };
  const [suggestion, setSuggestion] = useState<UserSuggestion | null>(null);
  const lookupTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEmailChange = (value: string) => {
    setContact((prev) => ({ ...prev, email: value, user_id: undefined }));
    setSuggestion(null);
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    if (!value.includes("@") || !value.includes(".")) return;
    lookupTimer.current = setTimeout(async () => {
      const result = await usersApi.lookup(value);
      setSuggestion(result);
    }, 400);
  };

  const handleLinkContact = () => {
    if (!suggestion) return;
    const nameParts = suggestion.display_name.split(" ");
    setContact((prev) => ({
      ...prev,
      user_id: suggestion.id,
      first_name: prev.first_name || nameParts[0] || "",
      last_name: prev.last_name || nameParts.slice(1).join(" ") || "",
    }));
    setSuggestion(null);
  };

  function handleSave() {
    onSave(contact);
    showNotification({
      type: "success",
      message: "Contact ajouté avec succès",
    });
    onClose();
  }

  return (
    <BaseModal
      title="Nouveau contact"
      startIcon={<Icon1 name="user" size={20}></Icon1>}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        {/* Email first — triggers lookup */}
        <div className="flex flex-col gap-1">
          <TextField
            label="Email"
            placeholder="marie.curie@life.com"
            value={contact.email || ""}
            onChange={handleEmailChange}
          />
          {contact.user_id && (
            <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium px-1">
              <span>✓</span>
              <span>Compte existant lié</span>
            </div>
          )}
          {suggestion && !contact.user_id && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
              <div className="w-7 h-7 rounded-full bg-blue-200 flex items-center justify-center text-xs font-medium text-blue-700 shrink-0">
                {suggestion.display_name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-neutral-800 truncate">
                  {suggestion.display_name}
                </div>
                <div className="text-xs text-neutral-400">Compte existant</div>
              </div>
              <button
                type="button"
                onClick={handleLinkContact}
                className="text-xs font-medium text-blue-600 hover:text-blue-800 whitespace-nowrap"
              >
                Lier ce compte
              </button>
              <button
                type="button"
                onClick={() => setSuggestion(null)}
                className="text-neutral-400 hover:text-neutral-600 text-sm leading-none"
              >
                ✕
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-row gap-2">
          <TextField
            label="Prénom"
            placeholder="Jean"
            value={contact.first_name}
            onChange={(value) =>
              setContact({ ...contact, first_name: value })
            }
          />
          <TextField
            label="Nom"
            placeholder="Dupont"
            value={contact.last_name}
            onChange={(value) =>
              setContact({ ...contact, last_name: value })
            }
          />
        </div>
        <div className="flex flex-row gap-2">
          <TextField
            label="Téléphone"
            placeholder="06 01 02 03 04"
            type="phone"
            value={contact.phone_number || ""}
            onChange={(value) =>
              setContact({ ...contact, phone_number: value })
            }
          />
          <DropDown
            options={[
              { value: "M.", label: "M." },
              { value: "Mme", label: "Mme" },
              { value: "Mlle", label: "Mlle" },
            ]}
            label="Civilité"
            value={contact.prefix}
            onSelect={(value) =>
              setContact({ ...contact, prefix: value as ContactInfos["prefix"] })
            }
          />
        </div>
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label="Enregistrer"
          onClick={handleSave}
        ></Button>
      </div>
    </BaseModal>
  );
};
